import React, { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useForm, useFormState } from 'react-final-form';
import { useRouter } from 'next/router';
import Icon from '../../Icon';
import { fromObjToUrl, getCategoriesData } from '../../../utils/helpers';
import {
  CategoriesContainer,
  CategoryContainer,
  CategoryLinkContainer,
  CategoryText
} from './styled';
import { NewSearchCategoriesProps, NewSearchCategoryProps } from './types';
import { useStaticData } from '../../../hooks/useStaticData';
import { getInitialCategoriesLinks } from '../../../utils/helpers/getInitialCategoriesLinks';
import { getCategories, getLocalDataQuery } from '../../../api/queries';
import { CategoriesQuery, LocalDataQuery } from '../../../graphql/graphql';
import { fillCategoriesWithData } from '../../../utils/helpers/fillCategoriesWithData';
import { BUILDING_TYPE_ID } from '../../../utils/helpers/constants';

const NewSearchCategory = memo(
  ({ icon, text, onClick, stat, isSelected, link }: NewSearchCategoryProps) => {
    const isLink = typeof link === 'string';
    const Component = isLink ? CategoryLinkContainer : CategoryContainer;

    const handle = useCallback(() => {
      onClick();
    }, [onClick]);

    return (
      <Component
        data-cy={stat}
        data-stat={stat}
        onClick={handle}
        onKeyPress={handle}
        $isSelected={isSelected}
        {...(isLink ? { href: link!.toString(), locale: false } : {})}
        {...(!isLink ? { role: 'button', tabIndex: 0 } : {})}
      >
        <Icon
          width={24}
          height={24}
          color="#fff"
          indent={false}
          IconComponent={icon}
        />
        <CategoryText tag="body_1">{text}</CategoryText>
      </Component>
    );
  }
);
NewSearchCategory.displayName = 'NewSearchCategory';

export const NewSearchCategories = memo(
  ({ onClick, selectedValue, asLinks = false }: NewSearchCategoriesProps) => {
    const router = useRouter();
    const { change } = useForm();
    const { values } = useFormState({
      subscription: { values: true }
    });

    const { data } = useStaticData<CategoriesQuery>(getCategories);
    const {
      t,
      i18n: { language }
    } = useTranslation();

    const categoriesData = useMemo(() => {
      const base = getCategoriesData({ data, language });
      const flat = base.flatMap(category => {
        if (category.id === BUILDING_TYPE_ID.FLAT_ALL)
          return category.children || [];
        return category;
      });
      return flat.filter(category => category.id !== BUILDING_TYPE_ID.FLAT_ALL);
    }, [data, language]);

    const filledCategories = useMemo(
      () => fillCategoriesWithData(categoriesData, false, t),
      [categoriesData, t]
    );

    const { data: localData } = useQuery<LocalDataQuery>(getLocalDataQuery, {
      variables: { id: values.city_id },
      skip: !asLinks
    });

    const handleClick = useCallback(
      (value: string) => {
        if (onClick) {
          onClick(value);
        } else {
          change?.('category_id', value);
          change?.('category_touched', true);
        }
      },
      [onClick, change]
    );

    const initialCategoriesLinks = useMemo(
      () => getInitialCategoriesLinks(router.asPath === '/kiraye'),
      [router.asPath]
    );

    const generateCategoryUrl = useCallback(
      (categoryId: string) => {
        if (!asLinks) return undefined;
        if (!localData) return initialCategoriesLinks[categoryId];
        const { url, ...queryObj } = router.query;
        const { as } = fromObjToUrl(
          { ...values, category_id: categoryId },
          localData,
          queryObj.sorting
        );
        return as;
      },
      [asLinks, localData, initialCategoriesLinks, router.query, values]
    );

    const onCategoryClickById = useMemo(() => {
      const map = new Map<string, () => void>();
      filledCategories.forEach(c => {
        const { id } = c;
        map.set(id, () => handleClick(id));
      });
      return map;
    }, [filledCategories, handleClick]);

    return (
      <CategoriesContainer data-cy="categories-container">
        {filledCategories.map(category => (
          <NewSearchCategory
            key={category.id}
            stat={category.searchFormStat}
            icon={category.icon}
            text={category.name}
            onClick={onCategoryClickById.get(category.id)!}
            isSelected={category.id === selectedValue}
            link={generateCategoryUrl(category.id)}
          />
        ))}
      </CategoriesContainer>
    );
  }
);
NewSearchCategories.displayName = 'NewSearchCategories';
